/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { FormValues } from './useSchema';

import { Form, FormikProps } from 'formik';
import PageContainer from 'components/PageContainer';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import LocationIcon from '@material-ui/icons/LocationOn';

import useTranslate from 'hooks/useTranslate';
import { useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';
import Button from 'components/Button';

import styles from './styles';

import { dateMask, dobPipe } from 'utils/masks';
import RestartButton from 'components/RestartButton';
import { Field, FieldProps } from 'formik';
import { FormTextField } from 'components/TextField';
import {
  AddressFields,
  SsnField,
  MobileField,
  AlternateMobileField,
  StudentApplication,
  LoyaltyNumberField,
} from 'components/form';
import CancelButton from 'features/CancelButton';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import useFieldCustomization from 'hooks/useFieldCustomization';
import withOptional from 'components/form/withOptional';
import { selectLockedFields } from 'state/creditApplication';
import { useSelector } from 'react-redux';

type PropTypes = FormikProps<FormValues> & {
  shouldGeolocate: boolean;
};

const UnifiedFullApplication: React.FC<PropTypes> = props => {
  const { isValid, isSubmitting, shouldGeolocate } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const { isRequired, isVisible } = useFieldCustomization();
  const lockedFields = useSelector(selectLockedFields);
  return (
    <Box mt={8}>
      <PageContainer maxWidth="md">
        <Form noValidate>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12}>
              <Title>
                {tMd('unified.prequal.offerPushApplication.reviewTitle')}{' '}
              </Title>
            </Grid>

            <AddressFields {...props} />

            {isVisible('emailAddress') && (
              <Grid item xs={12} sm={6}>
                <Field name="emailAddress">
                  {(fieldProps: FieldProps) => (
                    <FormTextField
                      id="creditAppEmail"
                      fieldProps={fieldProps}
                      required={isRequired('emailAddress')}
                      type="email"
                      autoComplete="email"
                      inputProps={{ maxLength: 50 }}
                      label={withOptional(
                        t,
                        'formLabel.emailAddress',
                        isRequired('emailAddress'),
                      )}
                    />
                  )}
                </Field>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Field name="dateOfBirth">
                {(fieldProps: FieldProps) => (
                  <FormTextField
                    id="creditAppDOB"
                    fieldProps={fieldProps}
                    label={t('formLabel.dateOfBirth')}
                    placeholder="mm/dd/yyyy"
                    mask={dateMask}
                    pipe={dobPipe}
                    disabled={lockedFields.dateOfBirth}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                  />
                )}
              </Field>
            </Grid>

            {isVisible('loyaltyNumber') && (
              <Grid item xs={12} sm={6}>
                <LoyaltyNumberField />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <SsnField />
            </Grid>

            <Grid item xs={12} sm={6}>
              <MobileField />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AlternateMobileField />
            </Grid>

            {shouldGeolocate ? (
              <Container maxWidth="sm">
                <Grid
                  container
                  direction="column"
                  justify="center"
                  alignItems="center"
                >
                  <LocationIcon fontSize="small" color="action" />
                  <p
                    css={styles.finePrint}
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'unified.prequal.offerPushApplication.geolocationReason',
                      ),
                    }}
                  />
                </Grid>
              </Container>
            ) : null}

            <Grid item xs={12}>
              <StudentApplication />
            </Grid>

            <Grid item xs={12}>
              <ActionButtonContainer>
                <Button
                  id="prequalOfferPushAppSubmit"
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                >
                  {t('unified.prequal.offerPushApplication.nextButton')}
                </Button>

                <RestartButton>
                  {tMd('unified.prequal.offerPushApplication.restartLink')}
                </RestartButton>
                <CancelButton />
              </ActionButtonContainer>
            </Grid>
          </Grid>
        </Form>
      </PageContainer>
    </Box>
  );
};

export default UnifiedFullApplication;
